import { can } from '../core/access';
import { esc, openModal, closeModal } from '../ui/modal';
import { fetchFarmClickerApps, appSupportsTask, clickerAppLabel, DEFAULT_TASK_TYPE } from './clicker-apps-shared';
import { accountNumberFieldHtml } from './clicker-shared';
import { recallClickerCreds, rememberClickerCreds } from './clicker-cred-memory';
import { amountGroupsRowHtml, readAmountBounds, readDepositBalance, wireAmountGroups } from './clicker-amount-fields';
import { t } from '../ui/lang';
import { flowFieldHtml, wireFlowField } from './clicker-flow-field';
/**
 * Reconfigure popup for a device whose clicker is already running, opened from the
 * gear next to the Stop control on the device stream. Shows the live launch params
 * (Task type + App first, then the secrets senior operators may edit) and a single
 * "Save" action that PUTs the new config. It does NOT restart the running clicker —
 * the updated params take effect on the next launch.
 *
 * `webUrl` (AdsPower profiles) adds the bank URL whose open tab the agent works in and the
 * Login field; Login / PIN / app password then hold what the flow signs in with (ING: client
 * number / access code) and are optional — a flow reading an open session (Paytm) needs none.
 * It also adds the lazy-polling toggle (the Mercado API device's rule, per profile).
 */
export async function showClickerRestartModal(farmId, udid, displayName, opts = {}) {
    const modal = openModal(`
        <h2 class="modal__title">${t('clicker.reconfigure_title', { device: esc(displayName) })}</h2>
        <div class="clicker-restart-body"><p class="settings-hint">${t('common.loading')}</p></div>
    `);
    // Wider than the default 420px modal: the form lays its fields (and the two amount
    // groups) out in two columns, which is what keeps this dialog inside the viewport.
    modal.classList.add('modal--clicker-device');
    const bodyEl = modal.querySelector('.clicker-restart-body');
    let cfg;
    try {
        const cr = await fetch(`/api/farms/${farmId}/clicker/devices`);
        if (cr.ok)
            cfg = (await cr.json())[udid];
    }
    catch { /* fall back to defaults below */ }
    const c = cfg ?? {
        pin: '', password: '', bucket_code: '', operators_group: '', tg_chats: '',
        max_initial_amount: null, min_initial_amount: null,
        deposit_min_balance: null, deposit_max_balance: null,
        task_type: DEFAULT_TASK_TYPE, app_name: '',
    };
    bodyEl.innerHTML = `
        <form class="clicker-restart-form" novalidate>
            <div class="modal__field">
                <label>${t('clicker.task_type')}</label>
                <select class="settings-input dc-type">
                    <option value="payout" ${c.task_type === 'payout' ? 'selected' : ''}>${t('clicker.task_payout')}</option>
                    <option value="deposit" ${c.task_type === 'deposit' ? 'selected' : ''}>${t('clicker.task_deposit')}</option>
                    <option value="auto" ${c.task_type === 'auto' ? 'selected' : ''}>${t('clicker.task_auto')}</option>
                </select>
            </div>
            <div class="modal__field">
                <label>${t('clicker.app')}</label>
                <select class="settings-input dc-app">
                    <option value="" disabled hidden selected>${t('clicker.select_app')}</option>
                </select>
            </div>
            ${flowFieldHtml()}
            ${opts.webUrl
        ? `<div class="modal__field">
                <label>${t('clicker.web_url')}</label>
                <input class="settings-input dc-web-url" type="url" autocomplete="off" placeholder="${t('clicker.web_url_ph')}" value="${esc(c.web_url ?? '')}" />
            </div>`
        : ''}
            ${opts.webUrl ? accountNumberFieldHtml(c.account_number) : ''}
            ${opts.webUrl
        ? `<div class="modal__field">
                <label class="settings-toggle">
                    <input class="dc-lazy" type="checkbox"${c.lazy_poll !== false ? ' checked' : ''} />
                    <span>${t('clicker.lazy_label')}</span>
                </label>
                <p class="modal__hint">${t('clicker.lazy_hint')}</p>
            </div>`
        : ''}
            ${can('clicker.secrets') && opts.webUrl
        ? `<div class="modal__field">
                <label>${t('clicker.web_login')}</label>
                <input class="settings-input dc-web-login" type="text" autocomplete="off" placeholder="${t('clicker.web_login_ph')}" value="${esc(c.web_login ?? '')}" />
                <p class="modal__hint">${t('clicker.web_login_hint')}</p>
            </div>`
        : ''}
            ${can('clicker.secrets')
        ? `<div class="modal__field">
                <label>${t('clicker.pin')}</label>
                <input class="settings-input dc-pin" type="text" autocomplete="off" value="${esc(c.pin)}" />
            </div>
            <div class="modal__field">
                <label>${t('clicker.app_password')}</label>
                <input class="settings-input dc-password" type="text" autocomplete="off" placeholder="${t('clicker.app_password_placeholder')}" value="${esc(c.password)}" />
            </div>`
        : ''}
            ${can('clicker.routing')
        ? `<div class="modal__field">
                <label>${t('clicker.bucket_code')}</label>
                <input class="settings-input dc-bucket-code" type="text" autocomplete="off" placeholder="${t('clicker.bucket_code_override_placeholder')}" value="${esc(c.bucket_code)}" />
            </div>
            <div class="modal__field">
                <label>${t('clicker.operators_group')}</label>
                <input class="settings-input dc-operators-group" type="text" autocomplete="off" placeholder="${t('clicker.operators_group_override_placeholder')}" value="${esc(c.operators_group)}" />
            </div>
            <div class="modal__field">
                <label>${t('clicker.tg_chats')}</label>
                <input class="settings-input dc-tg-chats" type="text" autocomplete="off" placeholder="${t('clicker.tg_chats_override_placeholder')}" value="${esc(c.tg_chats)}" />
            </div>`
        : ''}
            ${amountGroupsRowHtml({
        min: c.min_initial_amount,
        max: c.max_initial_amount,
        minPlaceholder: t('clicker.farm_default'),
        maxPlaceholder: t('clicker.farm_default'),
    }, {
        min: c.deposit_min_balance,
        max: c.deposit_max_balance,
        minPlaceholder: t('clicker.farm_default'),
        maxPlaceholder: t('clicker.farm_default'),
    })}
            <div class="settings-error modal__error dc-error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel dc-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit dc-restart">${t('common.save')}</button>
            </div>
        </form>
    `;
    wireAmountGroups(bodyEl);
    const form = bodyEl.querySelector('.clicker-restart-form');
    const pinInput = bodyEl.querySelector('.dc-pin');
    const passwordInput = bodyEl.querySelector('.dc-password');
    const bucketCodeInput = bodyEl.querySelector('.dc-bucket-code');
    const operatorsGroupInput = bodyEl.querySelector('.dc-operators-group');
    const tgChatsInput = bodyEl.querySelector('.dc-tg-chats');
    const webUrlInput = bodyEl.querySelector('.dc-web-url');
    const accountInput = bodyEl.querySelector('.dc-account');
    const lazyInput = bodyEl.querySelector('.dc-lazy');
    const webLoginInput = bodyEl.querySelector('.dc-web-login');
    const typeSel = bodyEl.querySelector('.dc-type');
    const appSel = bodyEl.querySelector('.dc-app');
    const errorEl = bodyEl.querySelector('.dc-error');
    const submitBtn = bodyEl.querySelector('.dc-restart');
    bodyEl.querySelector('.dc-cancel').addEventListener('click', closeModal);
    // App list is driven by the farm's apps for the selected task type (same rule as
    // the per-device launch panel). Empty selection falls back to the placeholder so
    // we never send an empty app name.
    let availableApps = [];
    const repopulateApps = () => {
        const task = typeSel.value;
        const prev = appSel.value;
        const opts = [`<option value="" disabled hidden selected>${t('clicker.select_app')}</option>`];
        for (const a of availableApps) {
            if (!appSupportsTask(a, task))
                continue;
            opts.push(`<option value="${esc(a.name)}">${esc(clickerAppLabel(a))}</option>`);
        }
        appSel.innerHTML = opts.join('');
        appSel.value = Array.from(appSel.options).some((o) => o.value === prev) ? prev : '';
    };
    // Each app carries its own PIN/password (task type is NOT part of the key). Seed the
    // memory from the device's own saved config so switching away and back restores it,
    // then swap PIN/password to the selected app's remembered secrets on every app change
    // (clearing them when that app has none, so one app's secret never lingers onto another).
    if ((pinInput || passwordInput) && c.app_name)
        rememberClickerCreds(farmId, udid, c.app_name, c.pin, c.password);
    const applyRemembered = () => {
        if (!pinInput && !passwordInput)
            return;
        const creds = appSel.value ? recallClickerCreds(farmId, udid, appSel.value) : undefined;
        if (pinInput)
            pinInput.value = creds?.pin ?? '';
        if (passwordInput)
            passwordInput.value = creds?.password ?? '';
    };
    // Per-device flow, listed for the (task type, app) pair currently chosen and limited to
    // what this farm's team may use — see clicker-flow-field, which the launch panel shares.
    const flowField = wireFlowField(bodyEl, {
        farmId,
        initial: c.payout_flow_id ?? null,
        initialBuiltIn: c.built_in_flow,
        picks: c.flow_picks ?? {},
        typeSel,
        appSel,
    });
    // type → app list → flow list: each step narrows the next.
    typeSel.addEventListener('change', () => { repopulateApps(); flowField.repopulate(); });
    appSel.addEventListener('change', () => { applyRemembered(); flowField.repopulate(); });
    void fetchFarmClickerApps(farmId)
        .then((apps) => { availableApps = apps; })
        .catch(() => { availableApps = []; })
        .finally(() => {
        repopulateApps();
        if (c.app_name && Array.from(appSel.options).some((o) => o.value === c.app_name)) {
            appSel.value = c.app_name;
        }
        // The flow list is keyed on the app, and the app is only known here — the two
        // fetches race, so re-filter once the selection is actually settled.
        flowField.repopulate();
    });
    const fail = (msg) => {
        errorEl.textContent = msg;
        errorEl.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = t('common.save');
    };
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.style.display = 'none';
        if (!appSel.value) {
            fail(t('clicker.select_an_app'));
            return;
        }
        const pin = pinInput ? pinInput.value.trim() : '';
        const password = passwordInput ? passwordInput.value.trim() : '';
        if (pinInput && !pin && !opts.webUrl) {
            fail(t('clicker.pin_required'));
            return;
        }
        if (passwordInput && !password && !opts.webUrl) {
            fail(t('clicker.app_password_required'));
            return;
        }
        const webUrl = webUrlInput ? webUrlInput.value.trim() : '';
        if (webUrlInput && !/^https?:\/\/\S+$/.test(webUrl)) {
            fail(t('clicker.web_url_required'));
            return;
        }
        const bounds = readAmountBounds(bodyEl);
        if ('error' in bounds) {
            fail(bounds.error);
            return;
        }
        const balance = readDepositBalance(bodyEl);
        if ('error' in balance) {
            fail(balance.error);
            return;
        }
        submitBtn.disabled = true;
        submitBtn.textContent = t('common.saving');
        try {
            const saveRes = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(udid)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...(pinInput ? { pin } : {}),
                    ...(passwordInput ? { password } : {}),
                    ...(bucketCodeInput ? { bucket_code: bucketCodeInput.value.trim() } : {}),
                    ...(operatorsGroupInput ? { operators_group: operatorsGroupInput.value.trim() } : {}),
                    ...(tgChatsInput ? { tg_chats: tgChatsInput.value.trim() } : {}),
                    ...(webUrlInput ? { web_url: webUrl } : {}),
                    ...(accountInput ? { account_number: accountInput.value.trim() } : {}),
                    ...(lazyInput ? { lazy_poll: lazyInput.checked } : {}),
                    ...(webLoginInput ? { web_login: webLoginInput.value.trim() } : {}),
                    max_initial_amount: bounds.max,
                    min_initial_amount: bounds.min,
                    deposit_min_balance: balance.min,
                    deposit_max_balance: balance.max,
                    task_type: typeSel.value,
                    app_name: appSel.value,
                    ...flowField.picked(),
                }),
            });
            if (!saveRes.ok) {
                fail((await saveRes.json()).error ?? t('common.failed_to_save'));
                return;
            }
            // Remember these secrets for this device's app (per-device, not shared).
            if (pinInput || passwordInput)
                rememberClickerCreds(farmId, udid, appSel.value, pin, password);
            closeModal();
        }
        catch {
            fail(t('clicker.network_error'));
        }
    });
}
